import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  ITimesheetAttachmentDTO,
  ICreateTimesheetDashboardEntryRequestDTO,
  IDepartmentDTO,
  IJobDTO,
  IJobValueDTO,
} from 'src/api/dtos';
import CommonTimesheetRepository from 'src/api/repositories/common/v1/timesheet.repository';
import TimesheetRepository from 'src/api/repositories/ess/v1/timesheet.repository';
import { IError } from 'src/api/types';
import { DepartmentSelector } from 'src/features/departments';
import { JobSelector, useLocalizedJobNames } from 'src/features/jobs';
import {
  AddIcon,
  DeleteIcon,
  DocumentUploadModal,
  FileChip,
  Modal,
  Select,
  WarningFilledIcon,
} from 'src/shared/components';
import { API_DATE_FORMAT, DAY_DATE_MONTH_FORMAT } from 'src/shared/constants';
import { PermissionsEnums } from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useEditingDurationInput, useEditingMemoInput, usePermissions } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { getErrorMessage } from 'src/shared/utils';
import { RootState } from 'src/store/types';

import { QuickEntryAddTypeEnums } from './QuickEntryCard.enums';

const NONE_ABSENCE_AND_OVERRIDE = 'none';
const NOTE_MAX_LENGTH = 512;
const MAX_FILES = 1;

interface Props {
  type: QuickEntryAddTypeEnums;
  open: boolean;
  onClose: () => void;
}

export const QuickEntryAddModal: FC<Props> = ({ type, open, onClose }) => {
  const { t } = useTranslate();
  const { settings } = useSelector((state: RootState) => state.user);
  const numberOfJobLevels = settings?.numberOfJobLevels;
  const localizedJobNames = useLocalizedJobNames(numberOfJobLevels);
  const hasDepartmentOrJobPermissions = usePermissions([
    PermissionsEnums.FEATUREDEPARTMENT_ALLOCATION_FUNCTION,
    PermissionsEnums.FEATUREJOB_COSTING_FUNCTION,
  ]);

  const {
    data: timeCategories,
    isLoading: isLoadingTimeCategories,
    isFetching: isFetchingTimeCategories,
  } = CommonTimesheetRepository.endpoints.useGetTimeCategoriesQuery(
    { isMemo: type === QuickEntryAddTypeEnums.EXPENSE },
    {
      selectFromResult: ({ data, ...results }) => ({ data: data ?? [], ...results }),
    },
  );

  const loadingTimeCategories = isLoadingTimeCategories || isFetchingTimeCategories;

  const [createTimesheetDashboardEntry] = TimesheetRepository.endpoints.useCreateTimesheetDashboardEntryMutation();

  /**
   * ATTACHMENTS
   */
  const [openDocumentUploadModal, setOpenDocumentUploadModal] = useState<boolean>(false);
  const [attachment, setAttachment] = useState<ITimesheetAttachmentDTO | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  Yup.setLocale({
    mixed: {
      required: t('global.requiredField'),
    },
  });

  const jobValueSchema = Yup.object().shape({
    value: Yup.string().nullable(),
    description: Yup.string().nullable(),
  });

  const jobSchema = Yup.object().shape({
    name: Yup.string().nullable(),
    level: Yup.number().nullable(),
    values: Yup.array().of(jobValueSchema),
  });

  const validationSchema = Yup.object().shape({
    duration: Yup.number().when([], {
      is: () => type === QuickEntryAddTypeEnums.DURATION,
      then: () => Yup.number().required(),
    }),
    amount: Yup.number().when([], {
      is: () => type === QuickEntryAddTypeEnums.EXPENSE,
      then: () => Yup.number().required(),
    }),
    timeCategoryId: Yup.number().when([], {
      is: () => type === QuickEntryAddTypeEnums.EXPENSE,
      then: () => Yup.number().required(),
    }),
    note: Yup.string(),
    jobs: Yup.array().of(jobSchema.nullable()),
  });

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    const today = moment().format(API_DATE_FORMAT);

    const payload: ICreateTimesheetDashboardEntryRequestDTO = {
      durationInSeconds: type === QuickEntryAddTypeEnums.DURATION ? values.duration : undefined,
      amount: type === QuickEntryAddTypeEnums.EXPENSE ? values.amount : undefined,
      date: today,
      departmentId: values.department?.id,
      jobs: values.jobs.map((job) => job?.values[0]?.value ?? null),
      timeCategoryId: values.timeCategoryId,
      note: values.note?.trim().length ? values.note.trim() : undefined,
      referenceId: attachment?.referenceId ?? null,
    };

    // Upload attachement and update the referenceId
    if (attachment?.file) {
      const referenceId = await handleUpload(attachment.file);
      if (referenceId) {
        payload.referenceId = referenceId;
      }
    }

    try {
      await createTimesheetDashboardEntry(payload).unwrap();

      toast.info(t('timesheets.youHaveSuccessfullySavedAnEntry'), {
        type: toast.TYPE.SUCCESS,
      });

      handleClose();
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  const { values, initialValues, isSubmitting, setFieldValue, handleSubmit, touched, errors, dirty, resetForm } =
    useFormik<{
      duration: number | undefined;
      amount: number | undefined;
      timeCategoryId: number | undefined;
      note: string | undefined;
      department: IDepartmentDTO | undefined;
      jobs: (IJobDTO | null)[];
    }>({
      initialValues: {
        duration: undefined,
        amount: undefined,
        timeCategoryId: undefined,
        note: '',
        department: undefined,
        jobs: [...Array(numberOfJobLevels)].map(() => null),
      },
      validationSchema,
      onSubmit: handleSave,
    });

  const {
    value: durationInput,
    onInputChange: onDurationInputChange,
    onInputBlur: onDurationInputBlur,
  } = useEditingDurationInput(values.duration, (duration) => setFieldValue('duration', duration), true);

  const {
    value: amountInput,
    onInputChange: onAmountInputChange,
    onInputBlur: onAmountInputBlur,
  } = useEditingMemoInput(values.amount, (amount) => setFieldValue('amount', amount));

  const isDisabled =
    type === QuickEntryAddTypeEnums.EXPENSE ? !values.timeCategoryId || !values.amount : !values.duration;
  const disabled = loadingTimeCategories || isSubmitting || !dirty || isEqual(initialValues, values) || isDisabled;
  const noteMaxLength = (values.note?.length ?? 0) >= NOTE_MAX_LENGTH;

  const handleAbsenceAndOverrideChange = (event: SelectChangeEvent<number>) => {
    const timeCategoryId = event.target.value;

    if (timeCategoryId === NONE_ABSENCE_AND_OVERRIDE) {
      setFieldValue('timeCategoryId', undefined);
      return;
    }

    const selectedTimeCategory = timeCategories.find((timeCategory) => timeCategory.id === Number(timeCategoryId));

    if (!selectedTimeCategory) {
      return;
    }

    setFieldValue('timeCategoryId', selectedTimeCategory.id);
  };

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length > NOTE_MAX_LENGTH) return;
    setFieldValue('note', value);
  };

  const handleDepartmentChange = (department: IDepartmentDTO) => {
    setFieldValue('department', department);
  };

  const handleJobChange = (level: number, name: string, jobValue: IJobValueDTO) => {
    setFieldValue(`jobs[${level - 1}]`, { level, name, values: [jobValue] });
  };

  const handleUpload = async (file: File) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('attachment', file);

      const { referenceId } = await CommonTimesheetRepository.uploadAttachment(formData);
      return referenceId;
    } catch (error) {
      setAttachment(null);
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.error(errorMessage);
      return null;
    }
  };

  const handleAddAttachment = (file?: File) => {
    setAttachment({
      referenceId: new Date().getTime().toString(),
      fileName: file?.name ?? null,
      contentType: file?.type ?? null,
      fileSizeInBytes: file?.size ?? null,
      file,
    });
    setOpenDocumentUploadModal(false);
  };

  const handleDeleteFile = (file?: File) => {
    if (!file) {
      return;
    }
    setAttachment(null);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      modalHeaderProps={{
        title: `${t('timesheets.addQuickEntry')} (${moment().format(DAY_DATE_MONTH_FORMAT)})`,
        onClose: handleClose,
      }}
      modalFooterProps={{
        submitText: t('global.save'),
        onSubmit: handleSubmit,
        disabled,
        loading: isSubmitting,
        closeText: t('global.cancel'),
        onClose: handleClose,
      }}
    >
      <Box display="grid" gridTemplateColumns="fit-content fit-content" alignItems="center" pt={2} gap={2}>
        {type === QuickEntryAddTypeEnums.DURATION && (
          <>
            <FormLabel required>{t('global.duration')}</FormLabel>
            <TextField
              placeholder="Ex. 8:00"
              value={durationInput}
              onChange={onDurationInputChange}
              onBlur={onDurationInputBlur}
              error={touched.duration && !!errors.duration}
              sx={{ width: 120 }}
            />
          </>
        )}
        {type === QuickEntryAddTypeEnums.EXPENSE && (
          <>
            <FormLabel required>{t('global.amount')}</FormLabel>
            <TextField
              placeholder="Ex. 45"
              value={amountInput}
              onChange={onAmountInputChange}
              onBlur={onAmountInputBlur}
              error={touched.amount && !!errors.amount}
              sx={{ width: 200 }}
            />
          </>
        )}
        {hasDepartmentOrJobPermissions && (
          <>
            <ComponentWithPermissions permissions={PermissionsEnums.FEATUREDEPARTMENT_ALLOCATION_FUNCTION}>
              <FormLabel>{t('timesheets.department')}</FormLabel>
              <DepartmentSelector
                department={values.department}
                onSelect={handleDepartmentChange}
                sx={{ width: 280 }}
              />
            </ComponentWithPermissions>
            <ComponentWithPermissions permissions={PermissionsEnums.FEATUREJOB_COSTING_FUNCTION}>
              {[...Array(numberOfJobLevels)].map((_, index) => (
                <Fragment key={index}>
                  <FormLabel>{localizedJobNames[index]}</FormLabel>
                  <JobSelector
                    level={index + 1}
                    date={moment().format(API_DATE_FORMAT)}
                    jobValue={values.jobs[index]?.values[0] ?? undefined}
                    onSelect={handleJobChange}
                    sx={{ width: 280 }}
                  />
                </Fragment>
              ))}
            </ComponentWithPermissions>
          </>
        )}
        <FormLabel
          sx={{ gridRow: type === QuickEntryAddTypeEnums.DURATION ? 'inherit' : 2 }}
          required={type === QuickEntryAddTypeEnums.DURATION ? false : true}
        >
          {type === QuickEntryAddTypeEnums.DURATION ? t('timesheets.addAbsencesAndOverrides') : t('global.category')}
        </FormLabel>
        <Box
          sx={{
            gridRow: type === QuickEntryAddTypeEnums.DURATION ? 'inherit' : 2,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <Select
            value={values.timeCategoryId ?? ''}
            onChange={handleAbsenceAndOverrideChange}
            disabled={loadingTimeCategories}
            displayEmpty
            sx={{ width: 200, height: 40 }}
            error={type === QuickEntryAddTypeEnums.EXPENSE && touched.timeCategoryId && !!errors.timeCategoryId}
          >
            <MenuItem disabled value={''} sx={{ display: 'none' }}>
              <Typography fontSize="0.875rem" color={(theme) => theme.palette.text.disabled}>
                {type === QuickEntryAddTypeEnums.DURATION ? t('global.select') : t('timesheets.exMeal')}
              </Typography>
            </MenuItem>
            {loadingTimeCategories && (
              <MenuItem>
                <Typography fontSize="0.875rem">{t('global.loading')}</Typography>
              </MenuItem>
            )}
            {!loadingTimeCategories && type === QuickEntryAddTypeEnums.DURATION && (
              <MenuItem value={NONE_ABSENCE_AND_OVERRIDE}>
                <Typography fontSize="0.875rem">{t('global.none')}</Typography>
              </MenuItem>
            )}
            {timeCategories.map((timeCategory, index) => (
              <MenuItem key={index} value={timeCategory.id}>
                <Typography fontSize="0.875rem">{timeCategory.description}</Typography>
              </MenuItem>
            ))}
          </Select>
          {loadingTimeCategories && <CircularProgress size={24} />}
        </Box>
        <Stack direction="column" gap={0.5} sx={{ gridColumn: '1/3' }}>
          <FormLabel>{t('global.note')}</FormLabel>
          <Stack direction="column" gap={0.5} width={type === QuickEntryAddTypeEnums.EXPENSE ? '92%' : '100%'}>
            <Stack direction="row" gap={1} alignItems="flex-start">
              <TextField
                value={values.note}
                onChange={handleNoteChange}
                error={noteMaxLength}
                placeholder={t('global.haveSomethingToSayAddItHere')}
                multiline
                sx={{ flex: 1 }}
              />
              <IconButton onClick={() => setFieldValue('note', '')} color="error">
                <DeleteIcon size={20} />
              </IconButton>
            </Stack>
            <Typography
              fontSize={12}
              color={(theme) => (noteMaxLength ? theme.palette.error.main : theme.palette.action.active)}
              sx={{ ml: 'auto', mr: 6, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {noteMaxLength ? (
                <>
                  <WarningFilledIcon size={16} />
                  {t('global.noteHasReachedCharacterLimit')}
                </>
              ) : (
                `${values.note?.length}/${NOTE_MAX_LENGTH}`
              )}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {type === QuickEntryAddTypeEnums.EXPENSE && (
        <Stack direction="column" spacing={2} mt={1}>
          {attachment && attachment.file && (
            <Stack direction="column" gap={0.5}>
              <FormLabel>{t('files.attachment')}</FormLabel>
              <FileChip canPreview file={attachment.file} onDeleteFile={handleDeleteFile} />
            </Stack>
          )}

          <ComponentWithPermissions permissions={PermissionsEnums.WTS_EXPENSE_ATTACHMENTS}>
            <Button
              color="secondary"
              disabled={!!attachment}
              startIcon={<AddIcon size={16} />}
              onClick={() => setOpenDocumentUploadModal(true)}
              sx={{ width: 'fit-content', fontSize: 12 }}
            >
              {t('files.addAttachment')}
            </Button>
          </ComponentWithPermissions>
        </Stack>
      )}
      <DocumentUploadModal
        maxFiles={MAX_FILES}
        open={openDocumentUploadModal}
        onClose={() => setOpenDocumentUploadModal(false)}
        loading={uploading}
        files={attachment?.file ? [attachment.file] : []}
        setFiles={(files) => {
          handleAddAttachment(files[0] ?? null);
        }}
        onDeleteFile={() => {
          setAttachment(null);
        }}
      />
    </Modal>
  );
};
